import { getAllClients } from '@api/FW/main.api';
import { readLocalStorageData, saveLocalStorageData } from '@app/services/localStorage.service';
import { notificationService } from '@app/services/notification.service';
import { LocalStorageItems } from '@app/shared/constants/localStorageItems';
import { Client } from '@interfaces/FW/Client';
import { UserModel } from '@interfaces/FW/UserModel';
import { createAction, createAsyncThunk, createSlice, PrepareAction } from '@reduxjs/toolkit';
import { t } from 'i18next';

export interface UserState {
	user: UserModel | null;
	allClients: Client[] | null;
	client: Client | null;
}

const initialState: UserState = {
	user: readLocalStorageData(LocalStorageItems.userPermissions),
	allClients: readLocalStorageData(LocalStorageItems.allClients),
	client: readLocalStorageData(LocalStorageItems.client),
};

/**
 * Update user permissions obj
 */
export const setUserPermissions = createAction<PrepareAction<UserModel>>(
	'user/setUser',
	(newUser) => {
		saveLocalStorageData(LocalStorageItems.userPermissions, newUser);

		return { payload: newUser };
	},
);

/**
 * Update selected client (tenant)
 */
export const setSelectedClient = createAction<PrepareAction<Client>>(
	'user/setSelectedClient',
	(newClient: Client, showNotification?: boolean) => {
		const tabTitle = document.title;

		if (newClient.nameFull !== tabTitle) {
			document.title = newClient.nameFull;
		}
		saveLocalStorageData(LocalStorageItems.client, newClient);

		if (showNotification) {
			notificationService.success({
				message: t('clientSwitch.changed'),
				description: t('clientSwitch.description', { name: newClient.nameFull }),
			});
		}

		return { payload: newClient };
	},
);

/**
 * Update all available clients (tenants)
 */
export const setAllClients = createAction<PrepareAction<Client[]>>(
	'user/setSelectedClients',
	(newClients: Client[]) => {
		saveLocalStorageData(LocalStorageItems.allClients, newClients);
		return { payload: newClients };
	},
);

/**
 * Refresh client data.
 */
export const refreshSelectedClient = createAsyncThunk(
	'user/refreshSelectedClient',
	async (clientId: string, { dispatch, rejectWithValue }) =>
		getAllClients()
			.then((clients) => {
				dispatch(setAllClients(clients));
				const currentClient = clients.find(({ counterparty }) => counterparty === clientId);
				if (!currentClient) return rejectWithValue('Client not found');
				dispatch(setSelectedClient(currentClient));
				notificationService.success({ message: 'CLIENT UPDATE' });
				return currentClient;
			})
			.catch((err) => rejectWithValue(err)),
);

const blankState: UserState = { user: null, client: null, allClients: null };

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(setUserPermissions, (state, action) => {
			state.user = action.payload;
		});
		builder.addCase(setSelectedClient, (state, action) => {
			state.client = action.payload;
		});
		builder.addCase(setAllClients, (state, action) => {
			state.allClients = action.payload;
		});
		builder.addCase('RESET_STORE' as any, () => blankState);
	},
});

export default userSlice.reducer;
